import { useCallback, useRef, useState } from "react";
import type { FollowUpWithRelations } from "@/hooks/use-follow-ups";
import type { FollowUpAction } from "@/lib/follow-up-calc";
import type { FollowUpChannel } from "@/lib/db-types";

/**
 * Tilstandsmaskin for utfallene på en oppfølging (CRM-2.4). Ett kort om gangen:
 *  e-post → send → neste steg → lukk (email_sent)
 *  telefon → notat → hva ble avtalt (VIB-2958):
 *    booket møte → book → lukk (phone_done, neste steg = møtet)
 *    ring igjen → neste steg med dato → lukk (phone_done)
 *    ikke aktuelt nå → avslutt med grunn
 *    (e-post og «fikk ikke svar» lagres direkte av dialogene)
 *  utsett → dato og notat
 *  avvis → lukkes med en gang, uten grunn (VIB-2986)
 * Lukkingen skjer først når neste steg er lagret (RPC-en krever det). Avbryter
 * brukeren «Neste steg», huskes den sendte e-posten (så den ikke sendes to
 * ganger) og notatet fra samtalen til oppfølgingen er lukket.
 */
export type FollowUpFlowStep =
  | { kind: "idle" }
  | { kind: "email"; followUp: FollowUpWithRelations }
  | { kind: "phone"; followUp: FollowUpWithRelations }
  | { kind: "postpone"; followUp: FollowUpWithRelations }
  | { kind: "close"; followUp: FollowUpWithRelations; note?: string | null }
  | { kind: "dismiss"; followUp: FollowUpWithRelations }
  | { kind: "book"; followUp: FollowUpWithRelations; note: string | null }
  | {
      kind: "next";
      followUp: FollowUpWithRelations;
      outcome: "email_sent" | "phone_done";
      note: string | null;
      activityId: string | null;
      /** Forhåndsutfylt neste steg (f.eks. «Ring igjen»). */
      preset?: { title: string; channel: FollowUpChannel };
    };

export interface FollowUpFlow {
  step: FollowUpFlowStep;
  start: (action: FollowUpAction, followUp: FollowUpWithRelations) => void;
  /** E-posten er sendt: videre til neste steg. */
  emailSent: (followUp: FollowUpWithRelations, activityId: string) => void;
  /** Utfall valgt i samtalen: booket møte, ring igjen eller ikke aktuelt nå. */
  phoneOutcome: (followUp: FollowUpWithRelations, note: string | null, outcome: "meeting" | "call_again" | "not_relevant") => void;
  /** Lukker dialogen hvis den fortsatt er på gitt steg (en sen onOpenChange skal ikke overstyre et nytt steg). */
  cancel: (kind?: FollowUpFlowStep["kind"]) => void;
  /** Ferdig; glemmer det som var husket for oppfølgingen. */
  finish: (followUpId?: string) => void;
  /** Notatet fra en avbrutt samtale-flyt, eller tom streng. */
  draftNote: (followUpId: string) => string;
}

interface Remembered {
  activityId?: string;
  note?: string;
}

export function useFollowUpFlow(): FollowUpFlow {
  const [step, setStepState] = useState<FollowUpFlowStep>({ kind: "idle" });
  const stepRef = useRef<FollowUpFlowStep>(step);
  const [remembered, setRemembered] = useState<Record<string, Remembered>>({});

  const setStep = useCallback((next: FollowUpFlowStep) => {
    stepRef.current = next;
    setStepState(next);
  }, []);

  const start = useCallback(
    (action: FollowUpAction, followUp: FollowUpWithRelations) => {
      const sent = remembered[followUp.id]?.activityId;
      if (action === "email" && sent) {
        // E-posten er allerede sendt: ikke send på nytt, gå rett til neste steg.
        setStep({ kind: "next", followUp, outcome: "email_sent", note: null, activityId: sent });
        return;
      }
      setStep(action === "close" ? { kind: "dismiss", followUp } : { kind: action, followUp });
    },
    [remembered, setStep],
  );

  const emailSent = useCallback(
    (followUp: FollowUpWithRelations, activityId: string) => {
      setStep({ kind: "next", followUp, outcome: "email_sent", note: null, activityId });
    },
    [setStep],
  );

  const phoneOutcome = useCallback(
    (followUp: FollowUpWithRelations, note: string | null, outcome: "meeting" | "call_again" | "not_relevant") => {
      if (outcome === "meeting") setStep({ kind: "book", followUp, note });
      else if (outcome === "not_relevant") setStep({ kind: "close", followUp, note });
      else {
        const name = followUp.customer?.name;
        setStep({
          kind: "next",
          followUp,
          outcome: "phone_done",
          note,
          activityId: null,
          preset: { title: name ? `Ring ${name} igjen` : "Ring igjen", channel: "phone" },
        });
      }
    },
    [setStep],
  );

  const cancel = useCallback(
    (kind?: FollowUpFlowStep["kind"]) => {
      const current = stepRef.current;
      if (kind && current.kind !== kind) return;
      if (current.kind === "next") {
        const id = current.followUp.id;
        setRemembered((r) => ({
          ...r,
          [id]: {
            activityId: current.activityId ?? r[id]?.activityId,
            note: current.note ?? r[id]?.note,
          },
        }));
      }
      if (current.kind === "book" || (current.kind === "close" && current.note)) {
        // Avbrutt møtebooking eller avslutning etter en samtale: notatet skal ikke gå tapt.
        const id = current.followUp.id;
        setRemembered((r) => ({ ...r, [id]: { ...r[id], note: current.note ?? r[id]?.note } }));
      }
      setStep({ kind: "idle" });
    },
    [setStep],
  );

  const finish = useCallback(
    (followUpId?: string) => {
      setStep({ kind: "idle" });
      if (followUpId) {
        setRemembered((r) => {
          if (!(followUpId in r)) return r;
          const { [followUpId]: _dropped, ...rest } = r;
          return rest;
        });
      }
    },
    [setStep],
  );

  const draftNote = useCallback((followUpId: string) => remembered[followUpId]?.note ?? "", [remembered]);

  return { step, start, emailSent, phoneOutcome, cancel, finish, draftNote };
}
